/**
 * FikoOzmoKafaDialogComponent - FikoYT Ozmo Kafa dialog component
 *
 * Dialog içinde FikoThreeJsComponent kullanarak Ozmo Kafa 3D viewer.
 * WTEC Diagonal Kafa ile aynı mimari ve optimizasyonlar.
 */

import {
  Component,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Injector,
  ViewContainerRef,
  EnvironmentInjector,
  NgZone
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';
import { FikoMini3DDialogBase } from './fiko-mini-3d-dialog.component';
import { getGPUAngleSearch, FikoGPUAngleSearch } from '../IFikoThreeJS/fiko-performance/FikoGPUAngleSearch';

declare var TWEEN: any;

@Component({
  selector: 'fiko-ozmo-kafa-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ozmo-kafa-container">
      <!-- 3D Viewer Container -->
      <div #viewerContainer class="viewer-container">
        <div #threeJsContainer></div>
        <!-- Konuma Sıfırla Butonu -->
        <button class="home-btn" (click)="kameraSifirla()" title="Başlangıç Konumuna Git">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
          </svg>
        </button>
      </div>

      <!-- Kontrol Paneli -->
      <div class="kontrol-paneli">
        <!-- I, J, K değerleri -->
        <div class="parametre-satiri">
          <label>I:</label>
          <input
            type="number"
            [(ngModel)]="iDeger"
            (ngModelChange)="ijkDegisti()"
            step="0.1"
            class="parametre-input"
          />
        </div>

        <div class="parametre-satiri">
          <label>J:</label>
          <input
            type="number"
            [(ngModel)]="jDeger"
            (ngModelChange)="ijkDegisti()"
            step="0.1"
            class="parametre-input"
          />
        </div>

        <div class="parametre-satiri">
          <label>K:</label>
          <input
            type="number"
            [(ngModel)]="kDeger"
            (ngModelChange)="ijkDegisti()"
            step="0.1"
            min="0"
            class="parametre-input"
          />
        </div>

        <!-- Üst Açı (B) Slider -->
        <div class="aci-kontrol">
          <div class="aci-baslik">
            <span>Üst Açı:</span>
            <span class="aci-deger">{{ ustAci | number:'1.3-3' }}°</span>
          </div>
          <input
            type="range"
            [(ngModel)]="ustAci"
            (ngModelChange)="ustAciDegisti($event)"
            min="0"
            max="360"
            step="0.005"
            class="slider cyan"
          />
        </div>

        <!-- Alt Açı (C) Slider -->
        <div class="aci-kontrol">
          <div class="aci-baslik">
            <span>Alt Açı:</span>
            <span class="aci-deger">{{ altAci | number:'1.3-3' }}°</span>
          </div>
          <input
            type="range"
            [(ngModel)]="altAci"
            (ngModelChange)="altAciDegisti($event)"
            min="-94"
            max="94"
            step="0.005"
            class="slider green"
          />
        </div>

        <!-- Açı Sapması -->
        <div class="sapma-satiri">
          Açı Sapması: {{ aciSapmasi | number:'1.3-3' }}°
        </div>

        <!-- Açıyı Bul Butonu -->
        <div class="buton-satiri">
          <button (click)="aciyiBul()" [disabled]="aciHesaplaniyor" class="ui mini blue button aciyibul-btn" [class.loading]="aciHesaplaniyor">
            <span *ngIf="aciHesaplaniyor" class="btn-spinner"></span>
            <span>{{ aciHesaplaniyor ? 'Hesaplanıyor...' : 'Açıyı Bul' }}</span>
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
    }

    .ozmo-kafa-container {
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding: 4px;
      background: rgba(0, 64, 64, 0.8);
      border-radius: 6px;
      min-width: 200px;
      width: 100%;
      max-width: 320px;
      height: 100%;
      box-sizing: border-box;
    }

    .viewer-container {
      position: relative;
      border-radius: 4px;
      overflow: hidden;
      border: 1px solid rgba(0, 255, 255, 0.3);
      background: #0a1628;
      flex: 1;
      min-height: 100px;
      touch-action: none;
    }

    .home-btn {
      position: absolute;
      bottom: 6px;
      right: 6px;
      width: 24px;
      height: 24px;
      padding: 0;
      background: rgba(0, 40, 60, 0.85);
      border: 1px solid rgba(255, 215, 0, 0.4);
      border-radius: 4px;
      color: #FFD700;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      z-index: 5;
    }

    .home-btn:hover {
      background: rgba(255, 215, 0, 0.2);
      border-color: #FFD700;
      box-shadow: 0 0 6px rgba(255, 215, 0, 0.4);
      transform: scale(1.1);
    }

    .home-btn:active {
      transform: scale(0.95);
    }

    .kontrol-paneli {
      display: flex;
      flex-direction: column;
      gap: 0.2rem;
      padding: 0.3rem;
      background: rgba(0, 20, 30, 0.5);
      border: 1px solid rgba(0, 200, 255, 0.15);
      border-radius: 2px;
    }

    .parametre-satiri {
      display: flex;
      align-items: center;
      gap: 0.2rem;
    }

    .parametre-satiri label {
      color: #00ccff;
      font-size: 10px;
      font-weight: 500;
      min-width: 12px;
    }

    .parametre-input {
      flex: 1;
      background: rgba(0, 50, 70, 0.6);
      border: 1px solid rgba(0, 200, 255, 0.3);
      color: #00ff88;
      padding: 0.15rem 0.2rem;
      font-size: 10px;
      border-radius: 2px;
      outline: none;
      transition: all 0.2s ease;
      max-width: 100%;
    }

    .parametre-input:focus {
      border-color: #00ccff;
      box-shadow: 0 0 3px rgba(0, 200, 255, 0.3);
    }

    .aci-kontrol {
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
      padding: 0.2rem 0;
    }

    .aci-baslik {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: #FFD700;
      font-size: 10px;
      font-weight: 500;
    }

    .aci-deger {
      color: #00ff88;
      font-weight: 600;
      font-size: 10px;
    }

    .slider {
      width: 100%;
      height: 3px;
      border-radius: 2px;
      outline: none;
      -webkit-appearance: none;
      appearance: none;
      padding: 0;
    }

    .slider.cyan {
      background: linear-gradient(to right, rgba(0, 255, 255, 0.2), #00ffff);
    }

    .slider.green {
      background: linear-gradient(to right, rgba(0, 255, 0, 0.2), #00ff00);
    }

    .slider::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #FFD700;
      cursor: pointer;
      box-shadow: 0 0 2px rgba(255, 215, 0, 0.5);
    }

    .slider::-moz-range-thumb {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #FFD700;
      cursor: pointer;
      border: none;
      box-shadow: 0 0 2px rgba(255, 215, 0, 0.5);
    }

    .sapma-satiri {
      text-align: center;
      color: #FFD700;
      font-size: 10px;
      font-weight: 500;
      padding: 0.2rem 0.3rem;
      background: rgba(255, 215, 0, 0.1);
      border: 1px solid rgba(255, 215, 0, 0.2);
      border-radius: 2px;
    }

    .buton-satiri {
      display: flex;
      gap: 0.2rem;
      flex-wrap: wrap;
      justify-content: flex-end;
      margin-top: 0.2rem;
      padding-top: 0.2rem;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .buton-satiri button {
      font-size: 9px !important;
      padding: 0.2rem 0.3rem !important;
      background-color: rgba(255, 215, 0, 0.1) !important;
      border: 1px solid rgba(255, 215, 0, 0.3) !important;
      color: #FFD700 !important;
      border-radius: 2px !important;
      height: 18px;
      transition: all 0.2s ease;
    }

    .buton-satiri button:hover {
      background-color: rgba(255, 215, 0, 0.2) !important;
      border-color: #FFD700 !important;
      box-shadow: 0 0 4px rgba(255, 215, 0, 0.3);
    }

    .buton-satiri button.blue {
      background-color: rgba(0, 120, 200, 0.3) !important;
      border-color: rgba(0, 150, 255, 0.5) !important;
      color: #00ccff !important;
    }

    .buton-satiri button.blue:hover {
      background-color: rgba(0, 150, 255, 0.4) !important;
      border-color: #00ccff !important;
      box-shadow: 0 0 4px rgba(0, 200, 255, 0.4);
    }

    .aciyibul-btn {
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
      gap: 4px;
      min-width: 70px;
      transition: all 0.3s ease;
    }

    .aciyibul-btn.loading {
      background: linear-gradient(90deg,
        rgba(0, 150, 255, 0.3) 0%,
        rgba(0, 200, 255, 0.5) 50%,
        rgba(0, 150, 255, 0.3) 100%
      ) !important;
      background-size: 200% 100% !important;
      animation: loading-shimmer 1.5s ease-in-out infinite;
      cursor: wait !important;
    }

    .aciyibul-btn:disabled {
      opacity: 0.9 !important;
    }

    .btn-spinner {
      width: 10px;
      height: 10px;
      border: 2px solid rgba(0, 200, 255, 0.3);
      border-top-color: #00ccff;
      border-radius: 50%;
      animation: spinner-rotate 0.8s linear infinite;
      flex-shrink: 0;
    }

    @keyframes spinner-rotate {
      to { transform: rotate(360deg); }
    }

    @keyframes loading-shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `]
})
export class FikoOzmoKafaDialogComponent extends FikoMini3DDialogBase {
  @ViewChild('threeJsContainer', { read: ViewContainerRef, static: true })
  override viewContainerRef!: ViewContainerRef;

  @ViewChild('viewerContainer', { static: true })
  viewerContainerRef!: ElementRef<HTMLDivElement>;

  // I, J, K değerleri
  iDeger: number = 0;
  jDeger: number = 0;
  kDeger: number = 1;

  // Açılar
  ustAci: number = 0;
  altAci: number = 0;
  aciSapmasi: number = 0;

  // Açı bulma algoritması parametreleri
  aciHesaplaniyor: boolean = false;
  // PERFORMANS: GPU ile aynı hassasiyet - açı sapması 0 olana kadar ara
  farkTolerans: number = 1e-6; // ~0.000001° hassasiyet
  aciBulmaAdimi: number = 0.01;

  // PERFORMANS: Reusable objeler - farkHesaplaSessiz için
  private readonly _tempCWorldDir = new THREE.Vector3();
  private readonly _tempHedefYon = new THREE.Vector3();

  // WebGPU Açı Arama - 100x hızlı paralel hesaplama
  private gpuAngleSearch: FikoGPUAngleSearch | null = null;
  private gpuAvailable: boolean | null = null; // null = henüz kontrol edilmedi

  // Ozmo Kafa sahne referansı
  private ozmoSahne?: any;

  constructor(
    cd: ChangeDetectorRef,
    injector: Injector,
    viewContainerRef: ViewContainerRef,
    environmentInjector: EnvironmentInjector,
    ngZone: NgZone
  ) {
    super(cd, injector, viewContainerRef, environmentInjector, ngZone);
    this.viewerGenislik = 200;
    this.viewerYukseklik = 180;
  }

  /**
   * Override: SahneOzmoKafa kullan
   */
  protected override async lazyLoadThreeJs(): Promise<void> {
    try {
      const { FikoThreeJsComponent } = await import('../fiko-three-js.component');
      const { SahneOzmoKafa } = await import('../Fiko3DComponent/sahne-kafa/SahneOzmoKafa');

      if (!FikoThreeJsComponent) {
        return;
      }

      this.createThreeJsComponent(FikoThreeJsComponent, SahneOzmoKafa);
    } catch (error) {
    }
  }

  override sahneHazir(): void {
    if (!this.sahne) {
      return;
    }

    try {
      // SahneOzmoKafa referansını sakla
      this.ozmoSahne = this.sahne;

      // B ve C açı parametrelerini bağla
      this.bindAciParametreleri();

      // Model yüklenene kadar bekle ve ekrana sığdır
      this.waitForModelAndFit();

      // Başlangıç açı sapmasını hesapla (I=0, J=0, K=1 için)
      setTimeout(() => {
        this.guncelleAciSapmasi();
        this.cd.detectChanges();
      }, 1500);
    } catch (error) {
    }
  }

  /**
   * Model yüklenene kadar bekle, sonra ekrana sığdır
   * Mesh sayısı artmayı durdurunca model yüklenmiş demektir
   * Animasyon yok - direkt pozisyon ayarlanır
   */
  private waitForModelAndFit(): void {
    let lastMeshCount = 0;
    let stableCount = 0;
    let attempts = 0;
    const maxAttempts = 15; // 3 saniye max

    const checkMeshes = () => {
      attempts++;

      let currentMeshCount = 0;
      this.sahne?.traverse((obj: any) => {
        if (obj instanceof THREE.Mesh) currentMeshCount++;
      });

      if (currentMeshCount > 0 && currentMeshCount === lastMeshCount) {
        stableCount++;
        if (stableCount >= 2) {
          // Model yüklendi - kayıtlı konuma git
          this.konumaGit();
          this.yenile();
          return;
        }
      } else {
        stableCount = 0;
        lastMeshCount = currentMeshCount;
      }

      if (attempts < maxAttempts) {
        setTimeout(checkMeshes, 200);
      } else {
        this.konumaGit();
        this.yenile();
      }
    };

    // Hemen başla
    setTimeout(checkMeshes, 100);
  }

  /**
   * Sahneyi yenile
   */
  protected yenile(): void {
    this.threeJs?.ciz();
    this.cd.detectChanges();
  }

  /**
   * B ve C açı parametrelerini bağla
   */
  private bindAciParametreleri(): void {
    if (!this.ozmoSahne?.BAcisi || !this.ozmoSahne?.CAcisi) {
      this.ustAci = 0;
      this.altAci = 0;
      return;
    }

    // İlk değerleri al
    this.ustAci = this.ozmoSahne.BAcisi.aci || 0;
    this.altAci = this.ozmoSahne.CAcisi.aci || 0;
  }

  /**
   * Ekrana sığdır - Dialog boyutuna göre optimize edilmiş
   * Animasyon yok, direkt pozisyon ayarla
   */
  protected ekranaSigdir(): void {
    if (!this.threeJs || !this.sahne) return;

    // Önce boyutu son kez düzelt
    this.fixRendererSize(this.threeJs);

    // Direkt ekrana sığdır - animasyon yok
    this.dialogEkranaSigdir();
  }

  /**
   * Dialog boyutlarına göre özel ekrana sığdırma
   * Animasyon YOK - sabit kamera pozisyonu
   */
  protected override dialogEkranaSigdir(): void {
    const kamera = this.threeJs?.aktifKamera as THREE.OrthographicCamera;
    const kontrol = this.threeJs?.aktifKontrol;

    if (!kamera || !kontrol) return;

    // Sabit kamera pozisyonu
    kamera.position.set(12.85, 26.98, -147.74);
    kamera.zoom = 0.52;
    kamera.updateProjectionMatrix();

    kontrol.target.set(31.82, 94.17, -194.32);
    kontrol.update();

    this.threeJs.ciz();
  }

  /**
   * Kayıtlı konuma git (Fikonum.1)
   */
  konumaGit(): void {
    const kaynakca = this.ozmoSahne?.kaynakca;

    if (!kaynakca?.konumlar?.git) {
      return;
    }

    // Fikonum.1 konumuna git
    kaynakca.konumlar.git('Fikonum.1');
  }

  /**
   * Kamerayı başlangıç konumuna sıfırla (Home butonu için)
   */
  kameraSifirla(): void {
    this.konumaGit();
    this.yenile();
  }

  /**
   * Mevcut kamera konumunu al ve console'a yaz
   */
  konumAl(): void {
    const kamera = this.threeJs?.aktifKamera as THREE.OrthographicCamera;
    const kontrol = this.threeJs?.aktifKontrol;

    if (!kamera || !kontrol) {
      return;
    }
  }

  /**
   * I, J, K değerleri değiştiğinde çağrılır
   * NOT: Normalize işlemi kaldırıldı - kullanıcı girdiği değerleri koruyor.
   * Normalize sadece hesaplamalarda (farkHesapla) yapılıyor.
   */
  ijkDegisti(): void {
    this.guncelleAciSapmasi();
    this.cd.detectChanges();
  }

  /**
   * Üst açı değiştiğinde
   */
  ustAciDegisti(deger: number): void {
    if (!this.ozmoSahne?.BAcisi) return;

    this.ustAci = deger;

    // SahneOzmoKafa'daki BAcisi'yi güncelle
    if (this.ozmoSahne.BAcisi.aci !== deger) {
      this.ozmoSahne.BAcisi.aci = deger;
      if (typeof this.ozmoSahne.BAcisi.degisti === 'function') {
        this.ozmoSahne.BAcisi.degisti(deger);
      }
    }

    this.guncelleAciSapmasi();
    this.threeJs?.ciz();
  }

  /**
   * Alt açı değiştiğinde
   */
  altAciDegisti(deger: number): void {
    if (!this.ozmoSahne?.CAcisi) return;

    this.altAci = deger;

    // SahneOzmoKafa'daki CAcisi'yi güncelle
    if (this.ozmoSahne.CAcisi.aci !== deger) {
      this.ozmoSahne.CAcisi.aci = deger;
      if (typeof this.ozmoSahne.CAcisi.degisti === 'function') {
        this.ozmoSahne.CAcisi.degisti(deger);
      }
    }

    this.guncelleAciSapmasi();
    this.threeJs?.ciz();
  }

  /**
   * Açı sapmasını güncelle
   */
  private guncelleAciSapmasi(): void {
    this.aciSapmasi = this.farkHesapla();
  }

  /**
   * PERFORMANS: Sessiz fark hesaplama (console.log yok, reusable objeler)
   * FIX: Matrix güncellemesi eklendi - Diagonal Kafa ile uyumlu
   */
  private farkHesaplaSessiz(): number {
    try {
      if (!this.ozmoSahne?.CAcisi?.takim) return 999;

      // KRİTİK FIX: degisti() lokal transform değiştiriyor ama matrix flag'lerini set etmiyor
      // Bu yüzden önce lokal matrix'i, sonra tüm parent zincirini güncellememiz gerekiyor

      // 1. BAcisi ve CAcisi'nin lokal matrix'lerini güncelle
      if (this.ozmoSahne.BAcisi) {
        this.ozmoSahne.BAcisi.updateMatrix();
      }
      if (this.ozmoSahne.CAcisi) {
        this.ozmoSahne.CAcisi.updateMatrix();
      }

      // 2. Takim'in lokal matrix'ini güncelle
      this.ozmoSahne.CAcisi.takim.updateMatrix();

      // 3. World matrix'i güncelle (parent zinciri dahil)
      this.ozmoSahne.CAcisi.takim.updateWorldMatrix(true, false);

      // PERFORMANS: Reusable Vector3'ler kullan - ALLOCATION YOK
      this.ozmoSahne.CAcisi.takim.getWorldDirection(this._tempCWorldDir);
      this._tempHedefYon.set(this.iDeger, this.jDeger, this.kDeger).normalize();

      const fark = Math.abs(this._tempCWorldDir.angleTo(this._tempHedefYon) * (180 / Math.PI));

      return fark;
    } catch (error) {
      return 999;
    }
  }

  /**
   * Kullanıcı için açı farkı hesapla (console.log ile)
   */
  farkHesapla(): number {
    return this.farkHesaplaSessiz();
  }

  /**
   * Açıyı bul - WebGPU + CPU Hybrid Algoritma
   *
   * WebGPU varsa: ~5ms paralel hesaplama (100x hızlı)
   * WebGPU yoksa: CPU fallback (adaptive arama)
   */
  async aciyiBul(): Promise<void> {
    if (this.aciHesaplaniyor) return;

    this.aciHesaplaniyor = true;
    this.cd.detectChanges();

    try {
      // WebGPU kullanılabilirlik kontrolü (lazy init)
      if (this.gpuAvailable === null) {
        this.gpuAngleSearch = getGPUAngleSearch();
        this.gpuAvailable = await this.gpuAngleSearch.isAvailable();
      }

      let b_en_kucuk = this.ustAci;
      let c_en_kucuk = this.altAci;

      if (this.gpuAvailable && this.gpuAngleSearch) {
        // ═══════════════════════════════════════════════════════════════
        // WebGPU YOLU - Sıfır fark araması (fark 0 olana kadar devam eder)
        // ═══════════════════════════════════════════════════════════════
        const startTime = performance.now();

        // Kalibrasyon değerlerini al
        const bKalibrasyon = this.ozmoSahne?.BAcisi?.kalibrasyon ?? 0;
        const cKalibrasyon = this.ozmoSahne?.CAcisi?.kalibrasyon ?? 0;

        // zeroSearch: fark 0'a yaklaşana kadar aramaya devam eder (kalibrasyon dahil)
        const result = await this.gpuAngleSearch.zeroSearch({
          targetI: this.iDeger,
          targetJ: this.jDeger,
          targetK: this.kDeger,
          bMin: 0,
          bMax: 360,
          cMin: -94,
          cMax: 94,
          bKalibrasyon,
          cKalibrasyon
        });

        if (result) {
          b_en_kucuk = result.bestB;
          c_en_kucuk = result.bestC;

          // ═══════════════════════════════════════════════════════════════
          // GPU + CPU HİBRİT: GPU sonucunu CPU ile doğrula ve 0'a ulaş
          // ═══════════════════════════════════════════════════════════════
          const bParam = this.ozmoSahne?.BAcisi;
          const cParam = this.ozmoSahne?.CAcisi;

          if (bParam && cParam) {
            // GPU sonucunu uygula ve gerçek farkı kontrol et
            bParam.aci = b_en_kucuk;
            bParam.degisti(b_en_kucuk);
            cParam.aci = c_en_kucuk;
            cParam.degisti(c_en_kucuk);

            let gercekFark = this.farkHesaplaSessiz();

            // Eğer gerçek fark > 0 ise CPU ile son ince ayar yap - TAM 0'A ULAŞMAK İÇİN
            if (gercekFark > 0.000001) {

              // Ultra hassas lokal arama - TAM 0'A ULAŞMAK İÇİN DAHA KÜCÜK ADIMLAR
              const hassasAdimlar = [0.01, 0.001, 0.0001, 0.00001, 0.000001];

              for (const adim of hassasAdimlar) {
                if (gercekFark < 0.000001) break; // Neredeyse 0

                const aralik = adim * 50;
                let enIyiFark = gercekFark;

                // C açısı lokal arama (Ozmo: -94 ile 94 arası)
                const cMin = Math.max(-94, c_en_kucuk - aralik);
                const cMax = Math.min(94, c_en_kucuk + aralik);
                for (let c = cMin; c <= cMax; c += adim) {
                  cParam.aci = c;
                  cParam.degisti(c);
                  const fark = this.farkHesaplaSessiz();
                  if (fark < enIyiFark) {
                    enIyiFark = fark;
                    c_en_kucuk = c;
                  }
                }
                cParam.aci = c_en_kucuk;
                cParam.degisti(c_en_kucuk);

                // B açısı lokal arama
                const bMin = Math.max(0, b_en_kucuk - aralik);
                const bMax = Math.min(360, b_en_kucuk + aralik);
                for (let b = bMin; b <= bMax; b += adim) {
                  bParam.aci = b;
                  bParam.degisti(b);
                  const fark = this.farkHesaplaSessiz();
                  if (fark < enIyiFark) {
                    enIyiFark = fark;
                    b_en_kucuk = b;
                  }
                }
                bParam.aci = b_en_kucuk;
                bParam.degisti(b_en_kucuk);

                gercekFark = enIyiFark;
              }
            }

            // Eski değerlere geri dön (animasyon için)
            bParam.aci = this.ustAci;
            bParam.degisti(this.ustAci);
            cParam.aci = this.altAci;
            cParam.degisti(this.altAci);
          }
        } else {
          // GPU araması başarısız oldu, CPU fallback
          await this.cpuAciyiBul((b, c) => {
            b_en_kucuk = b;
            c_en_kucuk = c;
          });
        }
      } else {
        // ═══════════════════════════════════════════════════════════════
        // CPU YOLU - Adaptive arama (~300-500ms)
        // ═══════════════════════════════════════════════════════════════
        await this.cpuAciyiBul((b, c) => {
          b_en_kucuk = b;
          c_en_kucuk = c;
        });
      }

      // Final animasyonlu update
      await this.animateToAngles(b_en_kucuk, c_en_kucuk);

    } finally {
      this.aciHesaplaniyor = false;
      this.cd.detectChanges();
    }
  }

  /**
   * CPU ile açı bulma - WebGPU olmadığında kullanılır
   * Açı sapması 0 olana kadar ara (GPU ile aynı hassasiyet)
   */
  private async cpuAciyiBul(sonucCallback: (b: number, c: number) => void): Promise<void> {
    let b_en_kucuk = this.ustAci;
    let c_en_kucuk = this.altAci;
    let iterasyon = 0;
    const maxIterasyon = 50; // Daha fazla iterasyon - 0'a ulaşmak için

    // AŞAMA 1: Kaba arama - TAM 360° (1° adım)
    await this.aramaYap(1, (b, c) => {
      b_en_kucuk = b;
      c_en_kucuk = c;
    });

    let mevcutFark = this.farkHesapla();

    // Fark toleransın altındaysa direkt dön
    if (mevcutFark < this.farkTolerans) {
      sonucCallback(b_en_kucuk, c_en_kucuk);
      return;
    }

    // AŞAMA 2: fark > 1 ise TAM 360° tarama (0.5° adım)
    if (mevcutFark > 1) {
      await this.aramaYap(0.5, (b, c) => {
        b_en_kucuk = b;
        c_en_kucuk = c;
      });
      mevcutFark = this.farkHesapla();
    }

    // AŞAMA 3: fark > 0.1 ise 0.1° adım
    if (mevcutFark > 0.1) {
      await this.aramaYap(0.1, (b, c) => {
        b_en_kucuk = b;
        c_en_kucuk = c;
      });
      mevcutFark = this.farkHesapla();
    }

    // AŞAMA 4: fark > 0.01 ise 0.01° adım
    if (mevcutFark > 0.01) {
      await this.aramaYap(0.01, (b, c) => {
        b_en_kucuk = b;
        c_en_kucuk = c;
      });
      mevcutFark = this.farkHesapla();
    }

    // AŞAMA 5: ULTRA HASSAS LOKAL ARAMA - 0'a ulaşana kadar
    // Adım boyutu: 0.001° → 0.0001° → 0.00001°
    const hassasAdimlar = [0.001, 0.0001, 0.00001, 0.000001];

    for (const adim of hassasAdimlar) {
      if (mevcutFark < this.farkTolerans) break;

      iterasyon = 0;
      while (mevcutFark >= this.farkTolerans && iterasyon < maxIterasyon) {
        iterasyon++;

        // Dinamik aralık: küçük adımda küçük aralık
        const aralik = Math.max(adim * 100, 0.01);

        await this.inceAramaYapLokal(b_en_kucuk, c_en_kucuk, adim, aralik, (b, c) => {
          b_en_kucuk = b;
          c_en_kucuk = c;
        });

        const yeniFark = this.farkHesapla();

        // İyileşme yoksa bir sonraki adıma geç
        if (Math.abs(yeniFark - mevcutFark) < adim * 0.1) {
          break;
        }

        mevcutFark = yeniFark;
      }
    }

    sonucCallback(b_en_kucuk, c_en_kucuk);
  }

  /**
   * TAM 360° arama - kaba arama için
   * FIX: Diagonal Kafa ile aynı mantık - eski değerleri kaydet, ara, geri yükle
   * FIX2: try-catch ile hata yakalama - Promise'ın kilitlenmesini önle
   */
  private aramaYap(adim: number, sonucCallback: (b: number, c: number) => void): Promise<void> {
    return new Promise((resolve) => {
      if (!this.ozmoSahne?.BAcisi || !this.ozmoSahne?.CAcisi) {
        sonucCallback(this.ustAci, this.altAci);
        resolve();
        return;
      }

      const bParam = this.ozmoSahne.BAcisi;
      const cParam = this.ozmoSahne.CAcisi;

      // KRİTİK FIX: UI değerlerini kullan - bParam.degeri animasyon sonrası farklı olabilir
      const eskiB = this.ustAci;
      const eskiC = this.altAci;

      // setTimeout ile UI thread'i serbest bırak
      setTimeout(() => {
        try {
          let aci_onceki_fark = 999;
          let b_en_kucuk = eskiB;
          let c_en_kucuk = eskiC;

          // B açısı TAM tarama (0-360) - Üst Açı
          // NOT: Ozmo'da "aci" property kullanılır, "degeri" değil!
          for (let i = 0; i <= 360; i += adim) {
            bParam.aci = i;
            bParam.degisti(i);
            const fark = this.farkHesaplaSessiz();
            if (fark < aci_onceki_fark) {
              aci_onceki_fark = fark;
              b_en_kucuk = i;
            }
          }

          // En iyi B değerine git
          bParam.aci = b_en_kucuk;
          bParam.degisti(b_en_kucuk);
          aci_onceki_fark = 999;

          // C açısı TAM tarama (-94 ile 94 arası) - Alt Açı
          for (let i = -94; i <= 94; i += adim) {
            cParam.aci = i;
            cParam.degisti(i);
            const fark = this.farkHesaplaSessiz();
            if (fark < aci_onceki_fark) {
              aci_onceki_fark = fark;
              c_en_kucuk = i;
            }
          }

          // KRİTİK FIX: Aramadan sonra ESKİ değerlere GERİ DÖN
          bParam.aci = eskiB;
          bParam.degisti(eskiB);
          cParam.aci = eskiC;
          cParam.degisti(eskiC);

          sonucCallback(b_en_kucuk, c_en_kucuk);
        } catch (error) {
          sonucCallback(eskiB, eskiC);
        } finally {
          resolve();
        }
      }, 0);
    });
  }

  /**
   * LOKAL ince arama - ±aralik içinde
   * FIX: Diagonal Kafa ile aynı mantık - eski değerleri kaydet, ara, geri yükle
   * FIX2: try-catch ile hata yakalama - Promise'ın kilitlenmesini önle
   */
  private inceAramaYapLokal(
    bMerkez: number,
    cMerkez: number,
    adim: number,
    aralik: number,
    sonucCallback: (b: number, c: number) => void
  ): Promise<void> {
    return new Promise((resolve) => {
      if (!this.ozmoSahne?.BAcisi || !this.ozmoSahne?.CAcisi) {
        sonucCallback(bMerkez, cMerkez);
        resolve();
        return;
      }

      const bParam = this.ozmoSahne.BAcisi;
      const cParam = this.ozmoSahne.CAcisi;

      // KRİTİK FIX: UI değerlerini kullan
      const eskiB = this.ustAci;
      const eskiC = this.altAci;

      setTimeout(() => {
        try {
          let b_en_kucuk = bMerkez;
          let c_en_kucuk = cMerkez;

          // Önce merkez değerlere git
          // NOT: Ozmo'da "aci" property kullanılır, "degeri" değil!
          bParam.aci = bMerkez;
          bParam.degisti(bMerkez);
          cParam.aci = cMerkez;
          cParam.degisti(cMerkez);

          let aci_onceki_fark = this.farkHesaplaSessiz();

          // B açısı LOKAL arama (±aralik içinde) - Üst Açı
          const bMin = Math.max(0, bMerkez - aralik);
          const bMax = Math.min(360, bMerkez + aralik);

          for (let i = bMin; i <= bMax; i += adim) {
            bParam.aci = i;
            bParam.degisti(i);
            const fark = this.farkHesaplaSessiz();
            if (fark < aci_onceki_fark) {
              aci_onceki_fark = fark;
              b_en_kucuk = i;
            }
          }

          bParam.aci = b_en_kucuk;
          bParam.degisti(b_en_kucuk);
          aci_onceki_fark = this.farkHesaplaSessiz();

          // C açısı LOKAL arama (±aralik içinde) - Alt Açı
          const cMin = Math.max(-94, cMerkez - aralik);
          const cMax = Math.min(94, cMerkez + aralik);

          for (let i = cMin; i <= cMax; i += adim) {
            cParam.aci = i;
            cParam.degisti(i);
            const fark = this.farkHesaplaSessiz();
            if (fark < aci_onceki_fark) {
              aci_onceki_fark = fark;
              c_en_kucuk = i;
            }
          }

          // KRİTİK FIX: ESKİ değerlere geri dön (animasyon için)
          bParam.aci = eskiB;
          bParam.degisti(eskiB);
          cParam.aci = eskiC;
          cParam.degisti(eskiC);

          sonucCallback(b_en_kucuk, c_en_kucuk);
        } catch (error) {
          sonucCallback(bMerkez, cMerkez);
        } finally {
          resolve();
        }
      }, 0);
    });
  }

  /**
   * Belirtilen B ve C açılarına animasyonlu geçiş yap
   * FIX: Diagonal Kafa'daki aciyaGit() ile aynı mantık
   * Stack overflow'u önlemek için onUpdate'de sadece degeri ayarlanır
   */
  private animateToAngles(
    bAcisina: number,
    cAcisina: number,
    bAcisindan: number = this.ustAci,
    cAcisindan: number = this.altAci
  ): Promise<void> {
    return new Promise((resolve) => {
      if (!this.ozmoSahne?.BAcisi || !this.ozmoSahne?.CAcisi) {
        resolve();
        return;
      }

      const bParam = this.ozmoSahne.BAcisi;
      const cParam = this.ozmoSahne.CAcisi;

      // Başlangıç değerlerine git
      // NOT: Ozmo'da "aci" property kullanılır, "degeri" değil!
      bParam.aci = bAcisindan;
      bParam.degisti(bAcisindan);
      cParam.aci = cAcisindan;
      cParam.degisti(cAcisindan);

      // Sürekli çizim başlat
      this.threeJs?.surekliCiz?.();

      if (typeof TWEEN === 'undefined') {
        // TWEEN yoksa direkt uygula
        bParam.aci = bAcisina;
        bParam.degisti(bAcisina);
        cParam.aci = cAcisina;
        cParam.degisti(cAcisina);

        this.ustAci = bAcisina;
        this.altAci = cAcisina;
        this.aciSapmasi = this.farkHesapla();

        this.yenile();
        resolve();
        return;
      }

      // B açısı animasyonu - Ozmo'da "aci" property kullanılır
      const bAnim = new TWEEN.Tween(bParam)
        .to({ aci: bAcisina }, 1000)
        .onUpdate(() => {
          bParam.degisti(bParam.aci);
          this.ustAci = bParam.aci;
        })
        .easing(TWEEN.Easing.Quadratic.Out);

      // C açısı animasyonu (750ms gecikme ile) - Diagonal Kafa ile aynı
      const cAnim = new TWEEN.Tween(cParam)
        .delay(750)
        .to({ aci: cAcisina }, 1000)
        .onUpdate(() => {
          cParam.degisti(cParam.aci);
          this.altAci = cParam.aci;
        })
        .easing(TWEEN.Easing.Quadratic.Out)
        .onComplete(() => {
          this.ngZone.run(() => {
            this.aciSapmasi = this.farkHesapla();
            this.threeJs?.surekliCizIptal?.();
            this.cd.detectChanges();
            resolve();
          });
        });

      bAnim.start();
      cAnim.start();

      this.cd.detectChanges();
    });
  }

}
